/**
 * Pure derivations for the public order-status page (screen 1g, adapted to the
 * simplified status enum: received → ready → collected | shipped).
 */

import { seedPricing } from '@rpl/pricing';
import type { FulfilmentKind, OrderStatus, OrderStatusResponse } from '../../lib/types';

/** Rank along the 3-step public timeline. Internal statuses map conservatively. */
const STATUS_RANK: Record<OrderStatus, number> = {
  received: 0,
  needs_review: 0,
  printing: 0,
  quality_check: 0,
  cancelled: 0,
  ready: 1,
  shipped: 2,
  collected: 2,
};

export function statusRank(status: OrderStatus): number {
  return STATUS_RANK[status] ?? 0;
}

/** Polling stops once the order can no longer advance. */
export function isTerminalStatus(status: OrderStatus): boolean {
  return status === 'collected' || status === 'shipped' || status === 'cancelled';
}

/** "2026-07-07T04:14:00Z" → "Tue 7 Jul, 2:14pm" (viewer's locale timezone). */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d
    .toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
    .replace(/\s/g, '')
    .toLowerCase();
  return `${day}, ${time}`;
}

/** ETA strings may be ISO dates or already-human text — format only real dates. */
export function formatEta(eta: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(eta)) return eta;
  const d = new Date(eta);
  if (Number.isNaN(d.getTime())) return eta;
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

export interface TimelineStepView {
  key: 'received' | 'ready' | 'final';
  label: string;
  state: 'done' | 'pending';
  caption: string | null;
  /** Set on the final ship step when a tracking link should render. */
  trackingUrl?: string | null;
}

/**
 * The 3-step timeline (Received → Ready → Collected/Shipped), gracefully
 * adapted from the 4-column mock. Done steps show their timeline timestamp;
 * the pending Ready step reassures ("we'll text you" / ETA).
 */
export function timelineSteps(order: OrderStatusResponse): TimelineStepView[] {
  const rank = statusRank(order.status);
  const ship = order.fulfilment === 'ship';
  const at = (status: OrderStatus): string | null =>
    order.timeline.find((t) => t.status === status)?.at ?? null;

  const receivedAt = at('received') ?? order.timeline[0]?.at ?? null;
  const readyAt = at('ready');
  const finalAt = ship ? at('shipped') : at('collected');
  const eta = order.eta ? formatEta(order.eta) : null;

  const readyPendingCaption = ship
    ? eta
      ? `Est. ${eta} · in the lab now`
      : 'In the lab now'
    : eta
      ? `Est. ${eta} · we’ll text you`
      : 'We’ll text you when it’s ready';

  return [
    {
      key: 'received',
      label: 'Received',
      state: 'done',
      caption: receivedAt ? formatWhen(receivedAt) : null,
    },
    {
      key: 'ready',
      label: ship ? 'Ready to ship' : 'Ready for pickup',
      state: rank >= 1 ? 'done' : 'pending',
      caption: rank >= 1 ? (readyAt ? formatWhen(readyAt) : null) : readyPendingCaption,
    },
    {
      key: 'final',
      label: ship ? 'Shipped' : 'Collected',
      state: rank >= 2 ? 'done' : 'pending',
      caption: rank >= 2 && finalAt ? formatWhen(finalAt) : null,
      ...(ship && rank >= 2 ? { trackingUrl: order.tracking_url } : {}),
    },
  ];
}

/** Display title varies with how far the order has come. */
export function statusTitle(order: OrderStatusResponse): string {
  const first = order.customer_first || 'friend';
  switch (order.status) {
    case 'ready':
      return `It’s ready, ${first}`;
    case 'shipped':
      return `On its way, ${first}`;
    case 'collected':
      return `Picked up. Enjoy, ${first}.`;
    case 'cancelled':
      return 'This order was cancelled';
    default:
      return `Order received, ${first}`;
  }
}

export interface PickupDetails {
  name: string;
  address: string;
  hours: string;
}

/**
 * Store blocks for the pickup card, from the bundled pricing config (the page
 * must render standalone, so we use the build-time seed — no live fetch).
 */
export function pickupDetails(kind: FulfilmentKind): PickupDetails | null {
  const f = seedPricing.fulfilment;
  const entry =
    kind === 'pickup_sydney'
      ? f['pickupSydney']
      : kind === 'pickup_wollongong'
        ? f['pickupWollongong']
        : null;
  if (!entry) return null;
  return {
    name: entry.label.replace(/^Pickup\s*—\s*/, ''),
    address: entry.address ?? '',
    hours: seedPricing.hours,
  };
}
